/* MenuItemsData.tsx */
import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronDownIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Dropdown } from "../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../ui/dropdown/DropdownItem";
import MenuCategory from "./subcomponents/RestuarantComponents/MenuItemComponents/MenuCategory";
import SearchBar from "../../ui/SearchBar";
import { LoadingSpinner } from "../../ui/LoadingSpinner";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { useUser } from "../../../context/UserInfoProvider";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { useMenuItems } from "../../../hooks/useMenuItems";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import BulkAddMenuItemsModal from "./subcomponents/BulkAddMenuItemsModal";
import { MenuItem } from "../../../types/menuItem";
import { DayOfWeek } from "../../../types/restaurant";
import { parseMenuItemError } from "../../../utils/menuItemErrorHandler";
import { useAuthenticatedApi } from "../../../hooks/useAuthenticatedApi";
// Removed direct API imports - using hooks instead

// Define allDays and formatTimeForDisplay locally or import if they are sharable utils
const allDays: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Helper to format time from HH:MM:SS or HH:MM to HH:MM for input fields
const formatTimeForInput = (timeString: string | null | undefined): string => {
  if (!timeString) return "";
  const parts = timeString.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return ""; // Should not happen with valid backend data
};

const formatTimeForDisplay = (timeString: string | null): string => {
  if (!timeString) return "No Data Available";
  const [hours, minutes] = timeString.split(":");
  const hour24 = parseInt(hours, 10);
  const minute = parseInt(minutes, 10);

  // Convert 24-hour to 12-hour format without timezone conversion
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? "PM" : "AM";

  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

const MenuItemsData: React.FC = () => {
  const { hotelId } = useUser();
  const analytics = useAnalytics();
  const { callApi } = useAuthenticatedApi();

  // Local state for restaurant selection (needed before hook)
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>("");

  // Use the useMenuItems hook for state management
  const {
    items: menuItems,
    restaurants,
    menus,
    selectedMenuId,
    setSelectedMenuId,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem: createItemFromHook,
    updateItem,
    deleteItem,
    fetchItems: refetchMenuItems,
    // Menu schedule state and functions
    menuSchedules,
    isLoadingMenuSchedules,
    menuSchedulesError,
    fetchMenuSchedules,
    updateMenuScheduleForDay,
    deleteMenuScheduleForDay,
  } = useMenuItems({
    hotelId: hotelId || "",
    restaurantId: selectedRestaurantId,
  });

  const [isRestaurantDropdownOpen, setIsRestaurantDropdownOpen] =
    useState(false);
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [initializationError, setInitializationError] = useState<string | null>(
    null
  );

  // Category data and category modifications
  const [categories, setCategories] = useState<string[]>([]);
  const [tempCategoryName, setTempCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null
  );

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDeletingItem, setIsDeletingItem] = useState<{
    [key: string]: boolean;
  }>({});
  const [isEditingItem] = useState<{
    [key: string]: boolean;
  }>({});
  const [isUpdatingCategoryItems, setIsUpdatingCategoryItems] = useState(false);

  // Bulk add state
  const [isBulkAddingItems, setIsBulkAddingItems] = useState(false);
  const [bulkAddError, setBulkAddError] = useState<string | null>(null);
  const [bulkAddRetainedRows, setBulkAddRetainedRows] = useState<
    Array<{
      itemname: string;
      category: string;
      description: string;
      ingredients: string;
      priceAmount: number;
      currencyCode: string;
    }> | null
  >(null);

  const [isScheduleSectionOpen, setIsScheduleSectionOpen] = useState(false);

  // State for editing menu schedules
  const [editingScheduleDay, setEditingScheduleDay] =
    useState<DayOfWeek | null>(null);
  const [tempScheduleTimes, setTempScheduleTimes] = useState<{
    open: string;
    close: string;
  }>({ open: "", close: "" });
  const [isSavingSchedule, setIsSavingSchedule] = useState<DayOfWeek | null>(
    null
  );
  const [scheduleUpdateError, setScheduleUpdateError] = useState<string | null>(
    null
  );

  // New state for managing restaurant transition and menu loading
  const [isTransitioningRestaurant, setIsTransitioningRestaurant] =
    useState(false);
  const [prevRestaurantId, setPrevRestaurantId] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  // Auto-select first restaurant if none selected
  useEffect(() => {
    if (restaurants.length > 0 && !selectedRestaurantId) {
      setSelectedRestaurantId(restaurants[0].restaurantId);
    }
  }, [restaurants, selectedRestaurantId]);

  // Restaurants and menus are now managed by the useMenuItems hook

  // Menu items are now managed by the useMenuItems hook

  // Update menu items functions - now using hook
  const updateMenuItem = useCallback(
    async (itemId: string, updates: Partial<MenuItem>) => {
      try {
        await updateItem(itemId, updates);
      } catch (err) {
        console.error("Error updating menu item:", err);
        throw err;
      }
    },
    [updateItem]
  );

  const deleteMenuItem = useCallback(
    async (itemId: string) => {
      try {
        await deleteItem(itemId);
      } catch (err) {
        console.error("Error deleting menu item:", err);
        throw err;
      }
    },
    [deleteItem]
  );

  const createItem = useCallback(
    async (itemData: Partial<MenuItem>) => {
      try {
        await createItemFromHook(
          itemData as Omit<
            MenuItem,
            | "menuitemid"
            | "hotelid"
            | "restaurantMenuId"
            | "createdat"
            | "updatedat"
          >
        );
      } catch (err) {
        console.error("Error creating menu item:", err);
        throw err;
      }
    },
    [createItemFromHook]
  );

  // Bulk add menu items handler
  const handleSaveBulkMenuItems = useCallback(
    async (
      itemsData: Array<{
        itemname: string;
        category: string;
        description: string;
        ingredients: string;
        priceAmount: number;
        currencyCode: string;
      }>
    ) => {
      if (!selectedMenuId || !selectedRestaurantId) {
        throw new Error("No menu or restaurant selected");
      }

      try {
        setIsUpdating(true);

        const results: Array<{
          itemname: string;
          success: boolean;
          error?: string;
          errorMessage?: string;
        }> = [];

        // Process all items and collect results
        for (const item of itemsData) {
          try {
            // Create price for the item
            const priceResponse = await callApi({
              method: "POST",
              url: `/management/prices`,
              data: {
                amount: item.priceAmount.toString(),
                currencyCode: item.currencyCode || "USD",
                priceType: "Menu Item",
                description: `Price for ${item.itemname}`,
              },
            });

            const createdPriceId = priceResponse.data?.priceId;
            if (!createdPriceId) {
              throw new Error("Failed to create price for menu item");
            }

            // Create the menu item
            await callApi({
              method: "POST",
              url: `/management/hotel/restaurants/${selectedRestaurantId}/menus/${selectedMenuId}/items`,
              data: {
                itemname: item.itemname,
                description: item.description || null,
                ingredients: item.ingredients || null,
                category: item.category || null,
                photo: null,
                price: createdPriceId,
              },
            });

            results.push({
              itemname: item.itemname,
              success: true,
            });
          } catch (err: any) {
            console.error(`Error creating menu item ${item.itemname}:`, err);
            const parsedError = parseMenuItemError(err);
            results.push({
              itemname: item.itemname,
              success: false,
              error: parsedError.userMessage,
              errorMessage:
                err?.response?.data?.error?.message || parsedError.userMessage,
            });
          }
        }

        // Check if there were any errors
        const failedResults = results.filter((result) => !result.success);
        const successCount = results.filter((result) => result.success).length;
        const totalCount = results.length;

        // If there are any errors, don't close modal and show inline errors
        if (failedResults.length > 0) {
          // Build detailed error message
          let errorMsg = `${failedResults.length} of ${totalCount} items failed to create. `;
          if (successCount > 0) {
            errorMsg += `${successCount} items were created successfully. `;
          }
          errorMsg += "Please review the errors below:\n\n";

          // Add specific error for each failed row
          failedResults.forEach((result) => {
            const rowNum =
              itemsData.findIndex(
                (item) => item.itemname === result.itemname
              ) + 1;
            errorMsg += `Row ${rowNum} (${result.itemname}): ${result.errorMessage}\n`;
          });

          setBulkAddError(errorMsg);

          // Persist failed rows so modal can rehydrate inputs after reload
          const failedRows = itemsData.filter((item) =>
            failedResults.some((r) => r.itemname === item.itemname)
          );
          setBulkAddRetainedRows(failedRows);

          // Refresh menu items for successful creates
          if (successCount > 0) {
            await refetchMenuItems();
            // Extract unique new categories from successful items
            const newCategories = new Set(categories);
            itemsData
              .filter((item) =>
                results.some(
                  (r) =>
                    r.itemname === item.itemname && r.success && item.category
                )
              )
              .forEach((item) => {
                if (item.category && !newCategories.has(item.category)) {
                  newCategories.add(item.category);
                }
              });
            setCategories(Array.from(newCategories));
          }

          return results; // Don't close modal, stay open for corrections
        }

        // If all successful, close modal and refresh
        await refetchMenuItems();
        
        // Extract unique new categories from all items
        const newCategories = new Set(categories);
        itemsData.forEach((item) => {
          if (item.category && !newCategories.has(item.category)) {
            newCategories.add(item.category);
          }
        });
        setCategories(Array.from(newCategories));

        setIsBulkAddingItems(false);
        setBulkAddError(null);
        setBulkAddRetainedRows(null);

        return results;
      } catch (err) {
        console.error("Error in bulk menu item creation:", err);
        setBulkAddError(
          err instanceof Error ? err.message : "Failed to create items"
        );
        return [];
      } finally {
        setIsUpdating(false);
      }
    },
    [
      selectedMenuId,
      selectedRestaurantId,
      callApi,
      refetchMenuItems,
      categories,
    ]
  );

  // Menu schedule functions - now using hook functions
  const upsertMenuSchedule = useCallback(
    async (day: DayOfWeek, open: string, close: string) => {
      if (!selectedRestaurantId || !selectedMenuId) return;

      try {
        await updateMenuScheduleForDay(day, open, close);
      } catch (err) {
        console.error("Error upserting menu schedule:", err);
        throw err;
      }
    },
    [selectedRestaurantId, selectedMenuId, updateMenuScheduleForDay]
  );

  const deleteMenuScheduleForDayWrapper = useCallback(
    async (day: DayOfWeek) => {
      if (!selectedRestaurantId || !selectedMenuId) return;

      try {
        await deleteMenuScheduleForDay(day);
      } catch (err) {
        console.error("Error deleting menu schedule:", err);
        throw err;
      }
    },
    [selectedRestaurantId, selectedMenuId, deleteMenuScheduleForDay]
  );

  // Effect to end the transition period
  useEffect(() => {
    if (isTransitioningRestaurant) {
      if (
        selectedMenuId &&
        menus.find(
          (m) =>
            m.restaurantMenuId === selectedMenuId &&
            m.restaurantId === selectedRestaurantId
        )
      ) {
        setIsTransitioningRestaurant(false);
      } else if (
        initializationError === "No menus found for the selected restaurant" &&
        menus.length === 0 &&
        !isLoading
      ) {
        setIsTransitioningRestaurant(false);
      }
    }
  }, [
    isTransitioningRestaurant,
    selectedMenuId,
    menus,
    initializationError,
    selectedRestaurantId,
    isLoading,
  ]);

  // Modified: Initialize restaurant selection
  useEffect(() => {
    if (restaurants.length > 0 && !selectedRestaurantId) {
      const firstRestaurant = restaurants[0];
      // prevRestaurantId will be null here, so this is the initial setup
      setPrevRestaurantId(selectedRestaurantId); // current selectedRestaurantId is "", so prev becomes ""
      setSelectedRestaurantId(firstRestaurant.restaurantId);
      setIsTransitioningRestaurant(true); // START TRANSITION
      setInitializationError(null);
    } else if (
      restaurants.length > 0 &&
      selectedRestaurantId &&
      prevRestaurantId === null &&
      selectedRestaurantId !== ""
    ) {
      // This condition handles if selectedRestaurantId is pre-filled (e.g. from query param later)
      // and this effect is running for the first time (prevRestaurantId is still null)
      setPrevRestaurantId(""); // Initialize prevRestaurantId to a non-null value to signify setup has occurred.
      setIsTransitioningRestaurant(true); // START TRANSITION
    } else if (restaurants.length === 0 && !isLoading) {
      setInitializationError("No restaurants found for this hotel");
    }
  }, [restaurants, selectedRestaurantId, prevRestaurantId, isLoading]);

  // Auto-select first menu when menus load and end transition
  useEffect(() => {
    if (menus.length > 0 && !selectedMenuId) {
      setSelectedMenuId(menus[0].restaurantMenuId);
      setIsTransitioningRestaurant(false); // End transition when menu is selected
    }
  }, [menus, selectedMenuId]);

  // Fetch menu schedules when menu changes
  useEffect(() => {
    if (selectedRestaurantId && selectedMenuId) {
      fetchMenuSchedules(selectedRestaurantId, selectedMenuId);
    }
  }, [selectedRestaurantId, selectedMenuId, fetchMenuSchedules]);

  useEffect(() => {
    // Initialize categories from menu items for the selected restaurant
    if (menuItems && Array.isArray(menuItems)) {
      const uniqueCategories = Array.from(
        new Set(menuItems.map((item) => item.category).filter(Boolean))
      );
      setCategories(uniqueCategories as string[]);
    } else {
      setCategories([]);
    }
  }, [menuItems]);

  const handleRemoveCategory = async (catId: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Category",
      message: `Are you sure you want to delete the category "${catId}"? This will delete all menu items within this category and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        setUpdateError(null);
        try {
          // Get all items in the category to be deleted
          const itemsToDelete = menuItems.filter(
            (item) => item.category === catId
          );

          // Delete each item
          // We can choose to await all deletions or let them run in parallel
          // For simplicity here, we'll await them sequentially.
          // Consider Promise.all for parallel deletion if performance is an issue for many items.
          for (const item of itemsToDelete) {
            await deleteMenuItem(item.menuitemid);
          }

          // If all items are deleted successfully (or if there were no items), remove the category from the list
          setCategories((prev) => prev.filter((cat) => cat !== catId));
        } catch (error) {
          console.error("Failed to delete items or category:", error);
          const parsedError = parseMenuItemError(error);
          setUpdateError(parsedError.userMessage);
          setFieldErrors(parsedError.fieldErrors);
        }
      },
    });
  };

  const handleStartEdit = (catId: string) => {
    setEditingCategoryId(catId);
    setTempCategoryName(catId);
  };

  const handleSaveCategoryName = async () => {
    const trimmedName = tempCategoryName.trim();
    const oldCategoryName = editingCategoryId; // Store the old name

    // Validation
    if (!trimmedName) {
      setUpdateError("Category name cannot be empty.");
      return;
    }
    if (editingCategoryId === "new" && categories.includes(trimmedName)) {
      setUpdateError("Category name already exists.");
      return;
    }
    if (
      editingCategoryId !== "new" &&
      categories.some((cat) => cat === trimmedName && cat !== editingCategoryId)
    ) {
      setUpdateError("Category name already exists.");
      return;
    }

    setUpdateError(null);

    if (editingCategoryId === "new") {
      setCategories((prev) => [trimmedName, ...prev]);
    } else if (oldCategoryName && oldCategoryName !== trimmedName) {
      // Update existing category name
      setCategories((prev) =>
        prev.map((cat) => (cat === oldCategoryName ? trimmedName : cat))
      );
      // Update items in this category
      const itemsToUpdate = menuItems.filter(
        (item) => item.category === oldCategoryName
      );
      setIsUpdatingCategoryItems(true);
      try {
        // Consider Promise.all for parallel updates if necessary
        for (const item of itemsToUpdate) {
          await updateMenuItem(item.menuitemid, { category: trimmedName });
        }
      } catch (error) {
        console.error("Failed to update items in renamed category:", error);
        const parsedError = parseMenuItemError(error);
        setUpdateError(parsedError.userMessage);
        setFieldErrors(parsedError.fieldErrors);
      } finally {
        setIsUpdatingCategoryItems(false);
      }
    }

    setEditingCategoryId(null);
    setTempCategoryName("");
  };

  const handleCancelCategoryNameEdit = () => {
    setEditingCategoryId(null);
    setTempCategoryName("");
  };

  const handleAddItem = (categoryId: string) => {
    console.log("Current state:", {
      hotelId,
      selectedMenuId,
      categoryId,
    });

    setSelectedItem({
      menuitemid: "",
      hotelid: hotelId || "",
      restaurantmenuid: selectedMenuId,
      photo: null,
      description: "",
      ingredients: "",
      category: categoryId,
      itemname: "",
      spicelevel: null,
      price: "",
      priceAmount: 0,
      currencyCode: "USD",
      isspecial: false,
      specialstartdate: null,
      specialenddate: null,
      createdat: new Date().toISOString(),
      updatedat: new Date().toISOString(),
    });
    setIsCreating(true);
  };

  // No separate helper needed

  // Availability is not used for restaurants; menu is view-only in guest app

  const handleDeleteItem = async (itemId: string) => {
    // Find the item to get its name for the confirmation dialog
    const item = menuItems.find((item) => item.menuitemid === itemId);
    const itemName = item?.itemname || "this item";

    setConfirmationDialog({
      isOpen: true,
      title: "Delete Menu Item",
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsDeletingItem((prev) => ({ ...prev, [itemId]: true }));
          await deleteMenuItem(itemId);
          // Refresh the menu items after deletion
          // await fetchMenuItems();
        } catch (error) {
          console.error("Error deleting menu item:", error);
          const parsedError = parseMenuItemError(error);
          setUpdateError(parsedError.userMessage);
          setFieldErrors(parsedError.fieldErrors);
        } finally {
          setIsDeletingItem((prev) => ({ ...prev, [itemId]: false }));
        }
      },
    });
  };

  // const handleEditItem = async (itemId: string) => {
  //   try {
  //     setIsEditingItem((prev) => ({ ...prev, [itemId]: true }));
  //     const item = menuItems.find((item) => item.menuitemid === itemId);
  //     if (item) {
  //       setSelectedItem(item);
  //     }
  //   } catch (error) {
  //     setUpdateError("Failed to edit menu item");
  //     console.error("Error editing menu item:", error);
  //   } finally {
  //     setIsEditingItem((prev) => ({ ...prev, [itemId]: false }));
  //   }
  // };

  // Availability UI removed

  // Filter categories based on search query (match category names OR any item name within the category)
  const searchLower = searchQuery.toLowerCase();
  const filteredCategories = categories.filter((cat) => {
    if (cat.toLowerCase().includes(searchLower)) return true; // direct category match

    // If any item inside the category matches on item name consider the category a match
    return menuItems.some(
      (item) =>
        item.category === cat &&
        item.itemname.toLowerCase().includes(searchLower)
    );
  });

  const handleItemSelect = (item: MenuItem) => {
    setSelectedItem(item);
  };

  const handleSave = async () => {
    if (!selectedItem) return;

    // Ensure hotel ID is available before proceeding
    if (!hotelId) {
      setUpdateError(
        "Hotel information not available. Please refresh the page."
      );
      return;
    }

    // Check if there's an ongoing upload

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setFieldErrors({});

      // Frontend validation for required fields when creating
      const missing: Record<string, string> = {};
      if (!selectedItem.itemname || selectedItem.itemname.trim() === "") {
        missing.itemname = "Item name is required";
      }
      const priceEmpty =
        (selectedItem.priceAmount ?? 0) <= 0 &&
        (!selectedItem.price || String(selectedItem.price).trim() === "");
      if (priceEmpty) {
        missing.price = "Price is required";
      }
      if (Object.keys(missing).length > 0) {
        setFieldErrors(missing);
        setUpdateError("Please fix the highlighted fields.");
        setIsUpdating(false);
        return;
      }

      if (isCreating) {
        await createItem(selectedItem);
        // After creating, immediately refetch items to get authoritative priceId/amount
        await refetchMenuItems();
      } else {
        const updatePayload: Partial<MenuItem> = {
          photo: selectedItem.photo,
          description: selectedItem.description,
          ingredients: selectedItem.ingredients,
          category: selectedItem.category,
          itemname: selectedItem.itemname,
          price: selectedItem.price,
          priceAmount: selectedItem.priceAmount,
          currencyCode: selectedItem.currencyCode,
        };
        await updateMenuItem(selectedItem.menuitemid, updatePayload);
        // Ensure updated server price or computed fields are reflected
        await refetchMenuItems();
      }

      // Track menu item management analytics
      if (selectedItem) {
        analytics.staff(
          isCreating ? "menu_item_created" : "menu_item_updated",
          {
            item_category: selectedItem.category?.toLowerCase(),
            has_photo: !!selectedItem.photo,
            price_amount: selectedItem.priceAmount,
            currency: selectedItem.currencyCode?.toLowerCase(),
          }
        );
      }

      // Close sidebar only after successful save
      if (selectedItem) {
        setSelectedItem(null);
        setIsCreating(false);
        setIsUpdating(false);
      }
    } catch (error: any) {
      // Parse error first to check if we got field-specific errors
      const parsedError = parseMenuItemError(error);
      
      // If we got a "too long" error but no specific field was identified,
      // check the selectedItem to find which field is likely too long
      const errorMessage = error?.response?.data?.error?.message || "";
      if (
        (errorMessage.includes("too long") || error?.code === "22001") &&
        Object.keys(parsedError.fieldErrors).length === 0 &&
        selectedItem
      ) {
        // Check field lengths against typical limits
        const fieldChecks = [
          { field: "itemname", value: selectedItem.itemname, maxLength: 255 },
          { field: "description", value: selectedItem.description, maxLength: 2000 },
          { field: "ingredients", value: selectedItem.ingredients, maxLength: 2000 },
          { field: "category", value: selectedItem.category, maxLength: 100 },
        ];
        
        // Find the longest field that exceeds typical limits
        for (const check of fieldChecks) {
          if (check.value && String(check.value).length > 200) {
            const fieldLabel = check.field === "itemname" ? "Item Name" : 
                              check.field === "description" ? "Description" :
                              check.field === "ingredients" ? "Ingredients" :
                              check.field === "category" ? "Category" : check.field;
            parsedError.fieldErrors[check.field] = `${fieldLabel} is too long. Please shorten it.`;
            parsedError.userMessage = `${fieldLabel} is too long. Please reduce the length.`;
            break;
          }
        }
      }
      
      // Now set the errors using the parsed (and potentially enhanced) error
      setUpdateError(parsedError.userMessage);
      setFieldErrors(parsedError.fieldErrors);
      
      setIsUpdating(false);
    }
  };

  const handleStartEditSchedule = (day: DayOfWeek) => {
    // Find all active schedules for this day, then pick the most recent one
    const activeSchedulesForDay = menuSchedules.filter(
      (s) =>
        s.dayOfWeek === day &&
        s.isActive &&
        s.restaurantMenuId === selectedMenuId
    );
    
    // If multiple schedules exist, prefer the first one (most recently added to array)
    // TODO: If backend provides updatedAt timestamp, use that to select most recent
    const activeScheduleForDay = activeSchedulesForDay.length > 0
      ? activeSchedulesForDay[0]
      : null;
    
    setEditingScheduleDay(day);
    setTempScheduleTimes({
      open: activeScheduleForDay
        ? formatTimeForInput(activeScheduleForDay.startTime)
        : "",
      close: activeScheduleForDay
        ? formatTimeForInput(activeScheduleForDay.endTime)
        : "",
    });
    setScheduleUpdateError(null);
  };

  const handleCancelEditSchedule = () => {
    setEditingScheduleDay(null);
    setTempScheduleTimes({ open: "", close: "" });
    setScheduleUpdateError(null);
  };

  const handleSaveSchedule = async (day: DayOfWeek) => {
    if (!selectedMenuId) return;
    setIsSavingSchedule(day);
    setScheduleUpdateError(null);

    const { open, close } = tempScheduleTimes;

    // Basic validation for HH:MM format
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if ((open && !timeRegex.test(open)) || (close && !timeRegex.test(close))) {
      setScheduleUpdateError(
        "Invalid time format. Please use HH:MM (e.g., 09:00 or 17:30)."
      );
      setIsSavingSchedule(null);
      return;
    }
    if (open && close && open >= close) {
      setScheduleUpdateError("Open time must be before close time.");
      setIsSavingSchedule(null);
      return;
    }

    try {
      if (!open && !close) {
        // If both are empty, consider it "Closed" by deactivating/deleting existing schedule
        const existingActiveSchedule = menuSchedules.find(
          (s) =>
            s.dayOfWeek === day &&
            s.isActive &&
            s.restaurantMenuId === selectedMenuId
        );
        if (existingActiveSchedule) {
          // Use deleteMenuScheduleForDay which sets isActive to false
          await deleteMenuScheduleForDayWrapper(day);
        }
      } else if (open && close) {
        // Check if there's an existing schedule for this day that needs to be updated
        // Find all schedules for this day (there may be multiple)
        const schedulesForDay = menuSchedules.filter(
          (s) => s.dayOfWeek === day && s.restaurantMenuId === selectedMenuId
        );
        
        // Prefer the most recently updated active schedule, or first active, or first overall
        const existingSchedule = schedulesForDay.find(s => s.isActive) || schedulesForDay[0];

        if (existingSchedule) {
          // Update existing schedule
          try {
            await upsertMenuSchedule(day, open, close);
          } catch (updateError) {
            console.error(`Failed to update schedule for ${day}:`, updateError);

            // If it's a conflict error, try deleting first then creating
            if (
              updateError instanceof Error &&
              updateError.message.includes("conflict")
            ) {
              try {
                await deleteMenuScheduleForDayWrapper(day);
                await new Promise((resolve) => setTimeout(resolve, 200));
                await upsertMenuSchedule(day, open, close);
              } catch (retryError) {
                console.error(
                  `Failed to retry schedule update for ${day}:`,
                  retryError
                );
                setScheduleUpdateError(
                  `Schedule update failed. Please try again.`
                );
                return;
              }
            } else {
              throw updateError;
            }
          }
        } else {
          // Create new schedule
          try {
            await upsertMenuSchedule(day, open, close);
          } catch (createError) {
            console.error(`Failed to create schedule for ${day}:`, createError);
            setScheduleUpdateError(
              `Failed to create schedule. Please try again.`
            );
            return;
          }
        }
      } else {
        // If only one time is provided, it's an invalid state for an active schedule
        setScheduleUpdateError(
          "Both open and close times are required to set an active schedule, or leave both blank for 'Closed'."
        );
        setIsSavingSchedule(null);
        return;
      }

      setEditingScheduleDay(null); // Exit edit mode on success
    } catch (error) {
      console.error(`Failed to save schedule for ${day}:`, error);
      setScheduleUpdateError(
        error instanceof Error ? error.message : "Failed to save schedule."
      );
    } finally {
      setIsSavingSchedule(null);
    }
  };

  // --- REFINED RETURN LOGIC ---

  // 0. Check if hotel ID is available (required for all operations)
  if (!hotelId) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600 dark:text-gray-400">
          Loading hotel information...
        </span>
      </div>
    );
  }

  if (isTransitioningRestaurant || isLoading) {
    return <PageLoadingView />;
  }

  // 2. Specific initialization error for "No restaurants found for this hotel"
  if (initializationError === "No restaurants found for this hotel") {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{initializationError}</p>
      </div>
    );
  }

  // 3. No restaurants found (after loading, if initializationError wasn't set for this specific message)
  if (restaurants.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>No restaurants found.</p>
      </div>
    );
  }

  // 4. Restaurant not selected (should be rare if auto-selection effect works)
  if (!selectedRestaurantId) {
    // This state implies restaurants are loaded, but selection is pending.
    // Could show a targeted loading message or rely on effects to select one.
    // For now, a generic message or brief spinner is okay.
    return (
      <div className="flex items-center justify-center h-full">
        <p>Selecting restaurant...</p>
      </div>
    );
  }

  // At this point: general isLoading is false, restaurants exist, one is selected.
  const selectedRestaurant = restaurants.find(
    (r) => r.restaurantId === selectedRestaurantId
  );

  // 5. Handle "No menus found" error, but only if menus array is actually empty.
  //    If menus.length > 0, it means menus just loaded, and this error might be stale from a previous state.
  if (
    initializationError === "No menus found for the selected restaurant" &&
    menus.length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{initializationError}</p>
      </div>
    );
  }

  // 6. General API error from the useMenuItems hook (e.g., for items or other unhandled cases)
  if (error) {
    return (
      <PageErrorView
        title="Error loading menu data"
        onRetry={refetchMenuItems}
      />
    );
  }

  // 7. Menus are loaded, but no menu is selected YET (e.g., auto-selection effect is pending).
  //    This is an intermediate state after restaurants are selected but before a menu is finalized.
  if (menus.length > 0 && !selectedMenuId) {
    return (
      <div className="flex justify-center items-center h-48">
        <LoadingSpinner size="sm" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">
          Loading menu details for {selectedRestaurant?.name}...
        </span>
      </div>
    );
  }

  // 8. Menus are definitively empty for the selected restaurant, and no menu is selected.
  //    This should ideally be caught by initializationError. This is a fallback.
  if (menus.length === 0 && !selectedMenuId && selectedRestaurantId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">
          No menus available for {selectedRestaurant?.name}. Please create one.
        </p>
      </div>
    );
  }

  // 9. Catch-all if a menu still isn't selected. This should be very rare.
  //    selectedMenuId is critical for the rest of the component.
  if (!selectedMenuId) {
    // This implies an issue with menu loading or selection logic that wasn't caught above.
    // Displaying a generic loading or error state before attempting to render the main UI.
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-orange-500">
          Menu information is currently unavailable. Please try refreshing or
          selecting another restaurant.
        </p>
      </div>
    );
  }

  // --- Main Content Rendering --- Checks passed, selectedMenuId should be valid.
  return (
    <div className="w-full p-6 font-sans text-gray-800 dark:text-white">
      {/* <div className="flex items-center justify-between"> ... rest of the component ... */}
      <div className="flex items-center justify-between overflow-visible">
        <div className="flex items-center gap-2 overflow-visible">
          <h1 className="text-2xl font-bold">{selectedRestaurant?.name}</h1>
          <div className="relative">
            <button
              onClick={() =>
                setIsRestaurantDropdownOpen(!isRestaurantDropdownOpen)
              }
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronDownIcon className="w-5 h-5 text-gray-800 dark:text-gray-400" />
            </button>
            <Dropdown
              isOpen={isRestaurantDropdownOpen}
              onClose={() => setIsRestaurantDropdownOpen(false)}
              className="min-w-[200px] z-[100]"
            >
              {restaurants.map((restaurant) => (
                <DropdownItem
                  key={restaurant.restaurantId}
                  onClick={() =>
                    setSelectedRestaurantId(restaurant.restaurantId)
                  }
                  className="whitespace-nowrap"
                >
                  {restaurant.name}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
          {menus.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
                className="flex items-center gap-1 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-sm"
              >
                {menus.find((menu) => menu.restaurantMenuId === selectedMenuId)
                  ?.menuName ||
                  (menus.find(
                    (menu) => menu.restaurantMenuId === selectedMenuId
                  )?.isRoomService
                    ? "Room Service"
                    : "Dine In")}
                <ChevronDownIcon className="w-4 h-4 text-gray-800 dark:text-gray-400" />
              </button>
              <Dropdown
                isOpen={isMenuDropdownOpen}
                onClose={() => setIsMenuDropdownOpen(false)}
                className="min-w-[200px] z-[100]"
              >
                {menus.map((menu) => (
                  <DropdownItem
                    key={menu.restaurantMenuId}
                    onClick={() => {
                      setSelectedMenuId(menu.restaurantMenuId);
                      setIsMenuDropdownOpen(false);
                    }}
                    className="whitespace-nowrap text-sm"
                  >
                    {menu.menuName ||
                      (menu.isRoomService ? "Room Service" : "Dine In")}
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>
          )}
        </div>

        {/* Remove the old menu selection section */}
        <div className="flex items-center gap-4">
          {menus.length === 0 && (
            <p className="text-gray-500">
              No menus found for selected restaurant
            </p>
          )}
        </div>
      </div>

      {/* Search and Add Category */}
      <div className="mt-8 flex items-center justify-between">
        <div className="w-full mr-4">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search menu items..."
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsBulkAddingItems(true)}
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-500 whitespace-nowrap"
          >
            Bulk Add Items
          </button>
          <button
            onClick={() => {
              setEditingCategoryId("new");
              setTempCategoryName("");
            }}
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 whitespace-nowrap"
          >
            + Add Category
          </button>
        </div>
      </div>

      {/* Menu Operating Hours Section */}
      {selectedMenuId && menus.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between w-full py-2">
            <span
              onClick={() => setIsScheduleSectionOpen(!isScheduleSectionOpen)}
              className="text-lg font-semibold text-gray-800 dark:text-white cursor-pointer"
            >
              Menu Operating Hours
            </span>
            <button
              onClick={() => setIsScheduleSectionOpen(!isScheduleSectionOpen)}
              className="p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full focus:outline-none"
            >
              <ChevronDownIcon
                className={`w-5 h-5 transition-transform ${
                  isScheduleSectionOpen ? "transform rotate-180" : ""
                }`}
              />
            </button>
          </div>
          {isScheduleSectionOpen && (
            <div className="mt-4 pl-2 space-y-3 border-l-2 border-gray-200 dark:border-gray-700">
              {isLoadingMenuSchedules ? (
                <div className="flex justify-center items-center h-24">
                  <LoadingSpinner />
                </div>
              ) : menuSchedulesError ? (
                <div className="text-red-500 text-center">
                  Error loading menu schedules: {menuSchedulesError.message}
                </div>
              ) : (
                allDays.map((day: DayOfWeek) => {
                  // Find all active schedules for this day with the correct menu ID
                  const activeSchedulesForDay = menuSchedules.filter(
                    (s) =>
                      s.dayOfWeek === day &&
                      s.isActive &&
                      s.restaurantMenuId === selectedMenuId
                  );
                  
                  // If multiple schedules exist, prefer the first one (most recently added to array)
                  // TODO: If backend provides updatedAt timestamp, use that to select most recent
                  const activeScheduleForDay = activeSchedulesForDay.length > 0
                    ? activeSchedulesForDay[0]
                    : null;
                  
                  const isEditingThisDay = editingScheduleDay === day;

                  return (
                    <div
                      key={day}
                      className="flex flex-col sm:flex-row justify-between sm:items-center border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 pt-1"
                    >
                      <span className="w-full sm:w-28 text-gray-700 dark:text-gray-300 mb-2 sm:mb-0">
                        {day}
                      </span>
                      {isEditingThisDay ? (
                        <div className="flex items-center">
                          <div className="text-right mr-2 flex-grow flex items-center justify-end gap-2">
                            <input
                              type="time"
                              value={tempScheduleTimes.open}
                              onChange={(e) =>
                                setTempScheduleTimes((prev) => ({
                                  ...prev,
                                  open: e.target.value,
                                }))
                              }
                              className="form-input w-24 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-1"
                            />
                            <span className="text-gray-500 dark:text-gray-400">
                              -
                            </span>
                            <input
                              type="time"
                              value={tempScheduleTimes.close}
                              onChange={(e) =>
                                setTempScheduleTimes((prev) => ({
                                  ...prev,
                                  close: e.target.value,
                                }))
                              }
                              className="form-input w-24 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-1"
                            />
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleSaveSchedule(day)}
                              disabled={isSavingSchedule === day}
                              className="p-1.5 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 disabled:opacity-50"
                            >
                              {isSavingSchedule === day ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <CheckIcon className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={handleCancelEditSchedule}
                              className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div className="text-right mr-2 flex-grow">
                            {activeScheduleForDay &&
                            activeScheduleForDay.startTime &&
                            activeScheduleForDay.endTime ? (
                              <span className="text-gray-800 dark:text-gray-300">
                                {formatTimeForDisplay(
                                  activeScheduleForDay.startTime
                                )}{" "}
                                -{" "}
                                {formatTimeForDisplay(
                                  activeScheduleForDay.endTime
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400">
                                No Data Available
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleStartEditSchedule(day)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {scheduleUpdateError && (
                <p className="text-red-500 text-sm mt-2 text-center">
                  {scheduleUpdateError}
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {/* END_MODIFICATION - Menu Schedule Section */}

      <div className="border-b border-gray-200 dark:border-gray-700 my-6"></div>

      {/* UI Indicator for category item updates */}
      {isUpdatingCategoryItems && (
        <div className="flex justify-center items-center my-4 p-4 bg-blue-50 dark:bg-gray-800 rounded-lg">
          <LoadingSpinner />
          <p className="ml-3 text-blue-700 dark:text-blue-300">
            Updating item categories...
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <PageErrorView
          title="Error loading menu data"
          onRetry={refetchMenuItems}
        />
      ) : (
        <div className="space-y-6 mt-6">
          {/* Show the new category input field FIRST if active */}
          {editingCategoryId === "new" && (
            <div className="border rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  className="flex-1 border rounded px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  value={tempCategoryName}
                  onChange={(e) => setTempCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveCategoryName}
                    className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelCategoryNameEdit}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 px-4 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {updateError && (
                <p className="text-red-500 text-sm mt-2">{updateError}</p>
              )}
            </div>
          )}

          {/* Show empty state message if no categories exist and not creating new */}
          {filteredCategories.length === 0 && editingCategoryId !== "new" ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
                  No items available
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  Create a category and add an item to fill this menu
                </p>
              </div>
            </div>
          ) : (
            /* Map through existing/filtered categories */
            filteredCategories.map((cat) => {
              const restaurantItems = menuItems.filter(
                (item) =>
                  item.category === cat &&
                  (searchLower === "" ||
                    item.itemname.toLowerCase().includes(searchLower) ||
                    cat.toLowerCase().includes(searchLower))
              );
              
              // Sort items by createdat ascending (oldest first, newest last)
              const sortedItems = [...restaurantItems].sort((a, b) => {
                const aCreated = a.createdat || "";
                const bCreated = b.createdat || "";
                if (aCreated && bCreated) {
                  return aCreated.localeCompare(bCreated);
                }
                if (aCreated && !bCreated) return -1;
                if (!aCreated && bCreated) return 1;
                return 0;
              });
              
              return (
                  <MenuCategory
                  key={cat}
                  category={{
                    id: cat,
                    name: cat,
                    items: sortedItems.map((item) => ({
                      id: item.menuitemid,
                      name: item.itemname,
                      price: item.price,
                      priceAmount: item.priceAmount,
                      currencyCode: item.currencyCode,
                      photo: item.photo,
                    })),
                  }}
                  isEditingName={editingCategoryId === cat}
                  tempName={tempCategoryName}
                  onEditName={setTempCategoryName}
                  onSaveName={handleSaveCategoryName}
                  onCancelEdit={handleCancelCategoryNameEdit}
                  onStartEdit={() => handleStartEdit(cat)}
                  onRemove={() => handleRemoveCategory(cat)}
                  onAddItem={() => handleAddItem(cat)}
                  onEditItem={(itemId) => {
                    const item = menuItems.find((i) => i.menuitemid === itemId);
                    if (item) handleItemSelect(item);
                  }}
                  onDeleteItem={(itemId) => handleDeleteItem(itemId)}
                  isDeletingItem={isDeletingItem}
                  isEditingItem={isEditingItem}
                />
              );
            })
          )}
        </div>
      )}

      {/* Sidebar */}
      <DataManagementSidebar
        title={isCreating ? "Add Menu Item" : "Edit Menu Item"}
        selectedItem={selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
        hideFooterError
        allCategories={categories}
        onSelectedItemChange={setSelectedItem}
      >
        {/* Conditionally render form fields only if selectedItem is not null */}
        {selectedItem && (
          <div className="space-y-4">
            {/* Show general error message at the top if hideFooterError is true */}
            {updateError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                  {updateError}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Photo
              </label>
              {/* Image uploading is not supported for this hotel on this page. Keeping the original uploader UI commented for future hotels. */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Photo uploading is disabled for this hotel.
                </p>
              </div>
              {/**
               * Original uploader UI (disabled for this hotel):
               *
               * {isCreating ? (
               *   <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
               *     <p className="text-sm text-gray-600 dark:text-gray-400">
               *       Please save the menu item first to upload a photo
               *     </p>
               *   </div>
               * ) : !selectedItem.menuitemid ? (
               *   <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center mt-2">
               *     <p className="text-sm text-gray-600 dark:text-gray-400">
               *       Menu item ID is missing. Please refresh the page or select a different item.
               *     </p>
               *   </div>
               * ) : (
               *   <div className="space-y-4 mt-2">
               *     <ImageUploader
               *       entityType="menuitem-photo"
               *       entityId={selectedItem.menuitemid}
               *       currentImageUrl={selectedItem.photo}
               *       onUploadComplete={handlePhotoUpload}
               *       onUploadError={(error) => {
               *         setUpdateError(error);
               *       }}
               *       onDeleteImage={() => {
               *         setSelectedItem({ ...selectedItem, photo: null });
               *       }}
               *       disabled={isUpdating}
               *     />
               *     {updateError && (
               *       <p className="text-red-500 text-sm mt-2">{updateError}</p>
               *     )}
               *   </div>
               * )}
               */}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <input
                type="text"
                value={selectedItem.itemname}
                onChange={(e) =>
                  setSelectedItem({ ...selectedItem, itemname: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {fieldErrors.itemname && (
                <p className="text-red-500 text-sm mt-1">
                  {fieldErrors.itemname}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={selectedItem.description || ""}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    description: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Ingredients
              </label>
              <textarea
                value={selectedItem.ingredients || ""}
                onChange={(e) =>
                  setSelectedItem({
                    ...selectedItem,
                    ingredients: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Price
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  {selectedItem.currencyCode}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    isCreating && selectedItem.priceAmount === 0
                      ? ""
                      : selectedItem.priceAmount
                  }
                  onChange={(e) =>
                    setSelectedItem({
                      ...selectedItem,
                      priceAmount:
                        e.target.value === ""
                          ? 0
                          : parseFloat(e.target.value) || 0,
                    })
                  }
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300"
                />
              </div>
              {fieldErrors.price && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors.price}</p>
              )}
            </div>
          </div>
        )}
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />

      {/* Bulk Add Menu Items Modal */}
      {isBulkAddingItems && (
        <BulkAddMenuItemsModal
          isOpen={true}
          onClose={() => {
            setIsBulkAddingItems(false);
            setBulkAddError(null);
            setBulkAddRetainedRows(null);
          }}
          existingCategories={categories}
          onSave={handleSaveBulkMenuItems}
          isCreating={isUpdating}
          error={bulkAddError}
          retainedRows={bulkAddRetainedRows || undefined}
        />
      )}
    </div>
  );
};

export default MenuItemsData;
